import { memo, useMemo } from 'react';

import defaultTimeZone from '@/constants/defaultTimeZone';

import { Tooltip } from '../Tooltip/Tooltip';
import { Typography } from '../Typography/Typography';

export interface DualTimeProps {
  dateTime: Date;
  memberTZ?: string | null;
  content?: React.ReactNode;
  showWeekday?: boolean;
  tooltipText?: string;
  /**
   * Whether to render a time-of-day (with timezone abbreviation) alongside the date.
   * Set to false for date-only values, e.g. cycle boundaries. Defaults to true.
   */
  showTime?: boolean;
  /**
   * Which timezone's value is shown as the primary (top-level) text vs. the tooltip.
   * Defaults to 'viewer', matching historical behavior (viewer's local time is primary,
   * member's time is in the tooltip).
   */
  primaryTimeZone?: 'viewer' | 'member';
  className?: string;
}

function DualTime({
  memberTZ,
  dateTime,
  content,
  showWeekday = false,
  showTime = true,
  primaryTimeZone = 'viewer',
  tooltipText,
  className
}: DualTimeProps) {
  const dateFormatOptions: Intl.DateTimeFormatOptions = useMemo(
    () => ({
      day: showTime ? 'numeric' : '2-digit',
      month: '2-digit',
      year: showTime ? '2-digit' : 'numeric',
      hour: showTime ? '2-digit' : undefined,
      minute: showTime ? '2-digit' : undefined,
      second: undefined,
      timeZoneName: showTime ? 'short' : undefined,
      weekday: showWeekday ? 'short' : undefined
    }),
    [showWeekday, showTime]
  );

  const memberTimeZone = memberTZ || defaultTimeZone;
  const isMemberPrimary = primaryTimeZone === 'member';
  const primaryTZ = isMemberPrimary ? memberTimeZone : undefined;
  const tooltipTZ = isMemberPrimary ? undefined : memberTimeZone;
  const resolvedTooltipText = tooltipText ?? (isMemberPrimary ? 'Your Time' : 'Member Time');

  return (
    <Tooltip
      content={
        <span>
          <dt>{resolvedTooltipText}</dt>
          <dd data-testid="memberTimeId">
            {dateTime.toLocaleString('en-us', {
              ...dateFormatOptions,
              timeZone: tooltipTZ
            })}
          </dd>
        </span>
      }>
      <Typography
        as="span"
        variant="body"
        className={className}
        style={{ width: 'max-content' }}
        data-testid="agentTimeId">
        {content ? <span>{content} </span> : ''}
        {dateTime.toLocaleString('en-us', { ...dateFormatOptions, timeZone: primaryTZ })}
      </Typography>
    </Tooltip>
  );
}

export default memo(DualTime);
export { DualTime };
